import React from 'react';
import { UseFormRegister, FieldErrors, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { cn } from '../../utils/tw';

interface ModuleSpecificFieldsProps {
  type: string;
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  control?: any; // Add control for Controller components
  errors: FieldErrors<any>;
  disabled?: boolean;
}

export const ModuleSpecificFields: React.FC<ModuleSpecificFieldsProps> = ({
  type,
  register,
  watch,
  setValue,
  control,
  errors,
  disabled = false
}) => {

  const renderCapitalFields = () => (
    <div className="space-y-4">
      {/* Item Name */}
      <div className="space-y-1">
        <div className="relative">
          <input
            id="itemName"
            type="text"
            {...register("itemName")}
            disabled={disabled}
            placeholder=" "
            maxLength={100}
            className={cn(
              'peer w-full border bg-card px-3 py-2 rounded-lg outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 placeholder-transparent min-h-[48px] transition-colors',
              errors.itemName && 'border-error focus:border-error focus:ring-error/30',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          />
          <label
            htmlFor="itemName"
            className="absolute left-3 top-1 text-xs text-mutedText transition-all peer-focus:-top-2 peer-focus:text-xs peer-focus:text-primary peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-mutedText"
          >
            Item Name <span className="text-error">*</span>
          </label>
        </div>
        {errors.itemName && (
          <p className="text-error text-xs">{errors.itemName.message as string}</p>
        )}
        <p className="text-xs text-mutedText">
          Enter the name or description of the capital asset
        </p>
      </div>

      {/* Is Consumable Toggle */}
      <div className="space-y-1">
        <div className="flex items-center gap-3">
          <input
            id="isConsumable"
            type="checkbox"
            {...register("isConsumable")}
            disabled={disabled}
            className="w-4 h-4 text-primary bg-card border-neutral rounded focus:ring-primary focus:ring-2"
          />
          <label htmlFor="isConsumable" className="text-sm font-medium text-text">
            Consumable Item
          </label>
        </div>
        <p className="text-xs text-mutedText">
          Check if this item will be consumed over time
        </p>
      </div>

      {/* Units Spent */}
      <div className="space-y-1">
        <div className="relative">
          <input
            id="unitsSpent"
            type="number"
            {...register("unitsSpent")}
            disabled={disabled}
            placeholder=" "
            min={0}
            step={0.01}
            className={cn(
              'peer w-full border bg-card px-3 py-2 rounded-lg outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 placeholder-transparent min-h-[48px] transition-colors',
              errors.unitsSpent && 'border-error focus:border-error focus:ring-error/30',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          />
          <label
            htmlFor="unitsSpent"
            className="absolute left-3 top-1 text-xs text-mutedText transition-all peer-focus:-top-2 peer-focus:text-xs peer-focus:text-primary peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-mutedText"
          >
            Units Spent
          </label>
        </div>
        {errors.unitsSpent && (
          <p className="text-error text-xs">{errors.unitsSpent.message as string}</p>
        )}
      </div>
    </div>
  );

  const renderPayrollFields = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Payout Mode */}
        <div className="space-y-1">
          <div className="relative">
            <select
              id="payoutMode"
              {...register("payoutMode")}
              disabled={disabled}
              className={cn(
                'peer w-full border bg-card px-3 py-2 rounded-lg outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 min-h-[48px] transition-colors',
                errors.payoutMode && 'border-error focus:border-error focus:ring-error/30',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <option value="">Select payout mode</option>
              <option value="time">Time-based</option>
              <option value="output">Output-based</option>
              <option value="monthly">Monthly</option>
              <option value="fixed">Fixed Amount</option>
            </select>
            <label
              htmlFor="payoutMode"
              className="absolute left-3 top-1 text-xs text-mutedText"
            >
              Payout Mode
            </label>
          </div>
          {errors.payoutMode && (
            <p className="text-error text-xs">{errors.payoutMode.message as string}</p>
          )}
        </div>

        {/* Payout Frequency */}
        <div className="space-y-1">
          <div className="relative">
            <select
              id="payoutFrequency"
              {...register("payoutFrequency")}
              disabled={disabled}
              className={cn(
                'peer w-full border bg-card px-3 py-2 rounded-lg outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 min-h-[48px] transition-colors',
                errors.payoutFrequency && 'border-error focus:border-error focus:ring-error/30',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <option value="">Select frequency</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
            <label
              htmlFor="payoutFrequency"
              className="absolute left-3 top-1 text-xs text-mutedText"
            >
              Payout Frequency
            </label>
          </div>
          {errors.payoutFrequency && (
            <p className="text-error text-xs">{errors.payoutFrequency.message as string}</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderAdminFields = () => (
    <div className="space-y-4">
      {/* Recurring Toggle */}
      <div className="space-y-1">
        <div className="flex items-center gap-3">
          <input
            id="recurring"
            type="checkbox"
            {...register("recurring")}
            disabled={disabled}
            className="w-4 h-4 text-primary bg-card border-neutral rounded focus:ring-primary focus:ring-2"
          />
          <label htmlFor="recurring" className="text-sm font-medium text-text">
            Recurring Expense
          </label>
        </div>
        <p className="text-xs text-mutedText">
          Check if this expense will repeat regularly
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Due Date */}
        <div className="space-y-1">
          <div className="relative">
            <input
              id="dueDate"
              type="date"
              {...register("dueDate")}
              disabled={disabled}
              placeholder=" "
              className={cn(
                'peer w-full border bg-card px-3 py-2 rounded-lg outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 placeholder-transparent min-h-[48px] transition-colors',
                errors.dueDate && 'border-error focus:border-error focus:ring-error/30',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            />
            <label
              htmlFor="dueDate"
              className="absolute left-3 top-1 text-xs text-mutedText transition-all peer-focus:-top-2 peer-focus:text-xs peer-focus:text-primary peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-mutedText"
            >
              Due Date
            </label>
          </div>
          {errors.dueDate && (
            <p className="text-error text-xs">{errors.dueDate.message as string}</p>
          )}
        </div>

        {/* Invoice Number */}
        <div className="space-y-1">
          <div className="relative">
            <input
              id="invoiceNo"
              type="text"
              {...register("invoiceNo")}
              disabled={disabled}
              placeholder=" "
              maxLength={50}
              className={cn(
                'peer w-full border bg-card px-3 py-2 rounded-lg outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 placeholder-transparent min-h-[48px] transition-colors',
                errors.invoiceNo && 'border-error focus:border-error focus:ring-error/30',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            />
            <label
              htmlFor="invoiceNo"
              className="absolute left-3 top-1 text-xs text-mutedText transition-all peer-focus:-top-2 peer-focus:text-xs peer-focus:text-primary peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-mutedText"
            >
              Invoice Number
            </label>
          </div>
          {errors.invoiceNo && (
            <p className="text-error text-xs">{errors.invoiceNo.message as string}</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderStockFields = () => (
    <div className="text-center py-8 text-gray-600">
      <p>All stock fields are configured in Section 1 (Expense Metadata).</p>
      <p className="text-sm text-gray-500 mt-2">Items dropdown and amount details are handled above.</p>
    </div>
  );

  // Render based on type
  switch (type?.toLowerCase()) {
    case 'capital':
      return renderCapitalFields();
    case 'payroll':
      return renderPayrollFields();
    case 'admin':
      return renderAdminFields();
    case 'stock':
      return renderStockFields();
    default:
      return (
        <div className="text-center py-8 text-mutedText">
          <p>No additional fields required for this expense type.</p>
        </div>
      );
  }
};

export default ModuleSpecificFields;
